import React, { useState } from 'react';
import { apiClient } from 'Utils/api';
import { useTranslation } from 'react-i18next';
import Header from 'View/Component/Header/Header';
import Footer from 'View/Component/Footer/Footer';
import PageContentRenderer from 'View/Component/PageContentRenderer/PageContentRenderer';
import * as S from './SellPage.style';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CONDITION_VALUES = ['NEW', 'LIKE_NEW', 'VERY_GOOD', 'GOOD', 'WORN', 'REPAIR_NEEDED'] as const;

interface FormData {
  brand: string;
  model: string;
  referenceNumber: string;
  yearOfProduction: string;
  condition: string;
  hasBox: boolean;
  hasPapers: boolean;
  desiredPrice: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  customerMessage: string;
}

const initialForm: FormData = {
  brand: '',
  model: '',
  referenceNumber: '',
  yearOfProduction: '',
  condition: '',
  hasBox: false,
  hasPapers: false,
  desiredPrice: '',
  customerName: '',
  customerEmail: '',
  customerPhone: '',
  customerMessage: '',
};

interface SectionProps {
  title: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  testId?: string;
}

const AccordionSection: React.FC<SectionProps> = ({ title, open, onToggle, children, testId }) => (
  <S.AccordionItem data-testid={testId}>
    <S.AccordionHeader type="button" onClick={onToggle} aria-expanded={open}>
      <span>{title}</span>
      <S.AccordionChevron open={open}>›</S.AccordionChevron>
    </S.AccordionHeader>
    {open && <S.AccordionBody>{children}</S.AccordionBody>}
  </S.AccordionItem>
);

const SellPage: React.FC = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormData>(initialForm);
  const [openSection, setOpenSection] = useState<string>('watch');
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const setField = (key: keyof FormData, value: any) =>
    setForm((p) => ({ ...p, [key]: value }));

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    if (imageUrls.length + files.length > 5) {
      setError(t('sell.errors.max_images'));
      return;
    }
    setUploading(true);
    setError('');
    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append('images', f));
      const res = await apiClient.post(`/api/sell-requests/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data?.urls) {
        setImageUrls((prev) => [...prev, ...res.data.urls]);
      }
    } catch (e: any) {
      setError(t('sell.errors.upload_failed'));
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (url: string) =>
    setImageUrls((prev) => prev.filter((u) => u !== url));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!form.brand || !form.model) {
      setError(t('sell.errors.brand_model_required'));
      setOpenSection('watch');
      return;
    }
    if (!form.customerName || !form.customerEmail) {
      setError(t('sell.errors.name_email_required'));
      setOpenSection('contact');
      return;
    }

    setSubmitting(true);
    try {
      const payload: any = {
        brand: form.brand,
        model: form.model,
        referenceNumber: form.referenceNumber || undefined,
        yearOfProduction: form.yearOfProduction ? parseInt(form.yearOfProduction, 10) : undefined,
        condition: form.condition || undefined,
        hasBox: form.hasBox,
        hasPapers: form.hasPapers,
        desiredPriceCents: form.desiredPrice ? Math.round(parseFloat(form.desiredPrice.replace(',', '.')) * 100) : undefined,
        imageUrls: imageUrls.length ? imageUrls : undefined,
        customerName: form.customerName,
        customerEmail: form.customerEmail,
        customerPhone: form.customerPhone || undefined,
        customerMessage: form.customerMessage || undefined,
      };
      const res = await apiClient.post(`/api/sell-requests`, payload);
      if (res.data?.status === 'success') {
        setSuccess(true);
        setForm(initialForm);
        setImageUrls([]);
      } else {
        setError(res.data?.message || t('sell.errors.submit_failed'));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('sell.errors.submit_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const toggle = (key: string) =>
    setOpenSection((prev) => (prev === key ? '' : key));

  if (success) {
    return (
      <>
        <Header />
        <S.PageContainer>
          <S.SuccessBox data-testid="sell-success">
            <S.SuccessIcon>✓</S.SuccessIcon>
            <S.SuccessTitle>{t('sell.success_title')}</S.SuccessTitle>
            <S.SuccessText>{t('sell.success_text')}</S.SuccessText>
            <S.PrimaryButton type="button" onClick={() => setSuccess(false)} data-testid="sell-new-request-btn">
              {t('sell.new_request')}
            </S.PrimaryButton>
          </S.SuccessBox>
        </S.PageContainer>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <S.PageContainer>
        <S.Headline data-testid="sell-headline">{t('sell.headline')}</S.Headline>

        <PageContentRenderer slug="verkaufen" />

        <S.FormCard onSubmit={handleSubmit} data-testid="sell-form">
          <AccordionSection
            title={t('sell.section_watch')}
            open={openSection === 'watch'}
            onToggle={() => toggle('watch')}
            testId="sell-section-watch"
          >
            <S.Row>
              <S.Field>
                <S.Label>{t('sell.label_brand')} *</S.Label>
                <S.Input
                  data-testid="sell-input-brand"
                  value={form.brand}
                  onChange={(e) => setField('brand', e.target.value)}
                  placeholder={t('sell.placeholder_brand')}
                />
              </S.Field>
              <S.Field>
                <S.Label>{t('sell.label_model')} *</S.Label>
                <S.Input
                  data-testid="sell-input-model"
                  value={form.model}
                  onChange={(e) => setField('model', e.target.value)}
                  placeholder={t('sell.placeholder_model')}
                />
              </S.Field>
            </S.Row>
            <S.Row>
              <S.Field>
                <S.Label>{t('sell.label_reference')}</S.Label>
                <S.Input
                  data-testid="sell-input-reference"
                  value={form.referenceNumber}
                  onChange={(e) => setField('referenceNumber', e.target.value)}
                  placeholder={t('sell.placeholder_reference')}
                />
              </S.Field>
              <S.Field>
                <S.Label>{t('sell.label_year')}</S.Label>
                <S.Input
                  data-testid="sell-input-year"
                  type="number"
                  value={form.yearOfProduction}
                  onChange={(e) => setField('yearOfProduction', e.target.value)}
                  placeholder={t('sell.placeholder_year')}
                />
              </S.Field>
            </S.Row>
          </AccordionSection>

          <AccordionSection
            title={t('sell.section_condition')}
            open={openSection === 'condition'}
            onToggle={() => toggle('condition')}
            testId="sell-section-condition"
          >
            <S.Field>
              <S.Label>{t('sell.label_condition')}</S.Label>
              <S.Select
                data-testid="sell-input-condition"
                value={form.condition}
                onChange={(e) => setField('condition', e.target.value)}
              >
                <option value="">{t('sell.select_placeholder')}</option>
                {CONDITION_VALUES.map((v) => (
                  <option key={v} value={v}>{t(`sell.conditions.${v}`)}</option>
                ))}
              </S.Select>
            </S.Field>
            <S.CheckboxRow>
              <S.CheckboxLabel>
                <input
                  data-testid="sell-input-box"
                  type="checkbox"
                  checked={form.hasBox}
                  onChange={(e) => setField('hasBox', e.target.checked)}
                />
                {t('sell.with_box')}
              </S.CheckboxLabel>
              <S.CheckboxLabel>
                <input
                  data-testid="sell-input-papers"
                  type="checkbox"
                  checked={form.hasPapers}
                  onChange={(e) => setField('hasPapers', e.target.checked)}
                />
                {t('sell.with_papers')}
              </S.CheckboxLabel>
            </S.CheckboxRow>
            <S.Field>
              <S.Label>{t('sell.label_price')}</S.Label>
              <S.Input
                data-testid="sell-input-price"
                type="text"
                inputMode="decimal"
                value={form.desiredPrice}
                onChange={(e) => setField('desiredPrice', e.target.value)}
                placeholder={t('sell.placeholder_price')}
              />
            </S.Field>
          </AccordionSection>

          <AccordionSection
            title={t('sell.section_images')}
            open={openSection === 'images'}
            onToggle={() => toggle('images')}
            testId="sell-section-images"
          >
            <S.UploadBox>
              <S.UploadLabel>
                <input
                  data-testid="sell-input-images"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => handleUpload(e.target.files)}
                  disabled={uploading || imageUrls.length >= 5}
                />
                {uploading ? t('sell.uploading') : t('sell.upload_button')}
              </S.UploadLabel>
              <S.UploadHint>{t('sell.upload_hint')}</S.UploadHint>
            </S.UploadBox>
            {imageUrls.length > 0 && (
              <S.ThumbGrid>
                {imageUrls.map((url) => (
                  <S.Thumb key={url}>
                    <img src={`${API_URL}${url}`} alt="" />
                    <S.ThumbRemove
                      type="button"
                      onClick={() => removeImage(url)}
                      aria-label={t('sell.remove_image')}
                    >×</S.ThumbRemove>
                  </S.Thumb>
                ))}
              </S.ThumbGrid>
            )}
          </AccordionSection>

          <AccordionSection
            title={t('sell.section_contact')}
            open={openSection === 'contact'}
            onToggle={() => toggle('contact')}
            testId="sell-section-contact"
          >
            <S.Field>
              <S.Label>{t('sell.label_name')} *</S.Label>
              <S.Input
                data-testid="sell-input-name"
                value={form.customerName}
                onChange={(e) => setField('customerName', e.target.value)}
              />
            </S.Field>
            <S.Row>
              <S.Field>
                <S.Label>{t('sell.label_email')} *</S.Label>
                <S.Input
                  data-testid="sell-input-email"
                  type="email"
                  value={form.customerEmail}
                  onChange={(e) => setField('customerEmail', e.target.value)}
                />
              </S.Field>
              <S.Field>
                <S.Label>{t('sell.label_phone')}</S.Label>
                <S.Input
                  data-testid="sell-input-phone"
                  value={form.customerPhone}
                  onChange={(e) => setField('customerPhone', e.target.value)}
                  placeholder={t('sell.phone_hint')}
                />
              </S.Field>
            </S.Row>
            <S.Field>
              <S.Label>{t('sell.label_message')}</S.Label>
              <S.Textarea
                data-testid="sell-input-message"
                rows={4}
                value={form.customerMessage}
                onChange={(e) => setField('customerMessage', e.target.value)}
                placeholder={t('sell.message_placeholder')}
              />
            </S.Field>
          </AccordionSection>

          {error && <S.ErrorMessage data-testid="sell-error">{error}</S.ErrorMessage>}

          <S.Actions>
            <S.PrimaryButton type="submit" disabled={submitting} data-testid="sell-submit-btn">
              {submitting ? t('sell.submitting') : t('sell.submit')}
            </S.PrimaryButton>
          </S.Actions>
        </S.FormCard>
      </S.PageContainer>
      <Footer />
    </>
  );
};

export default SellPage;
